/**
 * The Width measurement card: how the microscope photos are turned into
 * line widths and patch classes.
 *
 * Three settings of the width measurement config are read by the
 * measurement code: the width detection method, the patch classifier and
 * whether patches are classified by the width rule instead of the
 * classifier. The remaining keys of the document (version, calibration
 * method, sentinel value, ...) are not read by the current code and stay
 * as the template has them.
 */
import React from "react";
import { Stack, Typography } from "@mui/material";
import SettingFields, { getAt } from "./SettingFields";

export const QUANTIFICATION_METHODS = [
  { value: "faster_rcnn_OD_proposals", label: "Faster R-CNN line detection" },
  { value: "domain_adaptive_od_proposals", label: "Domain-adaptive line detection" },
  { value: "automated_with_nn_proposals", label: "Neural-network proposals" },
  { value: "automated_gaussian_proposals", label: "Gaussian proposals" },
  { value: "manual_with_gaussian_proposals", label: "Manual with Gaussian proposals" },
];

export const CLASSIFIER_METHODS = [
  { value: "nn", label: "Neural network" },
  { value: "yolo", label: "YOLO" },
  { value: "random", label: "Random (testing only)" },
];

export const WIDTH_FIELDS = [
  {
    path: ["quantification_method"],
    label: "Width detection",
    kind: "choice",
    options: QUANTIFICATION_METHODS,
    help: "The model that finds the lines in a patch photo",
  },
  {
    path: ["patch_classification", "method"],
    label: "Patch classifier",
    kind: "choice",
    options: CLASSIFIER_METHODS,
    help: "Marked, unmarked or burnt",
  },
  {
    path: ["patch_classification", "method_params", "use_width_classify"],
    label: "Classify by width rule",
    kind: "boolean",
    help: "Unmarked when no width, burnt above 0.10 mm; else the classifier decides",
  },
];

export function widthSummary(config) {
  const method = QUANTIFICATION_METHODS.find((option) => option.value === getAt(config, ["quantification_method"]));
  if (!method) return "";
  const rule = getAt(config, ["patch_classification", "method_params", "use_width_classify"]);
  const classifier = CLASSIFIER_METHODS.find((option) => option.value === getAt(config, ["patch_classification", "method"]));
  return `${method.label} • ${rule ? "width rule" : classifier?.label || "classifier"}`;
}

export default function WidthMeasurementCard({ config, onConfigChange, onInvalidChange }) {
  const loaded = config && config.quantification_method !== undefined;
  if (!loaded) {
    return (
      <Typography variant="body2" color="text.secondary">
        Loading the width measurement settings.
      </Typography>
    );
  }
  return (
    <Stack spacing={1.5} sx={{ width: "100%" }}>
      <Typography variant="body2" color="text.secondary">
        Every patch is photographed under the microscope after marking; these settings decide how its line width
        is measured and how it is classified.
      </Typography>
      <SettingFields
        config={config}
        fields={WIDTH_FIELDS}
        onChange={onConfigChange}
        onInvalidChange={onInvalidChange}
        testPrefix="width"
      />
    </Stack>
  );
}
